<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { useAuth } from '@/composition/use-auth';
  import { normalizeText } from '@/domain/pos/search-normalize';
  import { PermissionType } from '@/enums/PermissionType';
  import { ITable } from '@/interfaces/ITable';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const props = defineProps<{
    /** Undefined while the branch's tables are loading (or after a failed load). */
    tables?: ITable[];
    selectedIds: number[];
    loading: boolean;
    loadError: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:selected-ids', ids: number[]): void;
    (e: 'retry'): void;
  }>();

  const router = useRouter();
  const auth = useAuth();
  const restaurantStore = useRestaurantStore();
  const query = ref('');

  const canCreateTables = computed(() =>
    auth.hasPermission(PermissionType.TablesManage)
  );

  const hasQuery = computed(() => normalizeText(query.value.trim()).length > 0);

  const filteredTables = computed(() => {
    const tables = props.tables ?? [];
    const term = normalizeText(query.value.trim());

    if (!term) return tables;

    return tables.filter((table) => normalizeText(table.name).includes(term));
  });

  function isSelected(id: number) {
    return props.selectedIds.includes(id);
  }

  function onToggle(id: number, event: Event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement)) return;

    const ids = input.checked
      ? [...props.selectedIds, id]
      : props.selectedIds.filter((selectedId) => selectedId !== id);

    emit('update:selected-ids', ids);
  }

  /** With a search active, "select all" / "clear" act on the visible rows only. */
  function selectAll() {
    const visibleIds = filteredTables.value.map((table) => table.id);
    const ids = hasQuery.value
      ? [...new Set([...props.selectedIds, ...visibleIds])]
      : visibleIds;

    emit('update:selected-ids', ids);
  }

  function clearAll() {
    const visibleIds = new Set(filteredTables.value.map((table) => table.id));
    const ids = hasQuery.value
      ? props.selectedIds.filter((id) => !visibleIds.has(id))
      : [];

    emit('update:selected-ids', ids);
  }

  function goToTables() {
    const stringId = restaurantStore.restaurant?.stringId;

    if (stringId) {
      router.push(`/${stringId}/tables`).catch(() => {
        // Navigation cancelled or duplicated: nothing to recover.
      });
    }
  }
</script>

<template>
  <div class="tables-picker">
    <div v-if="loadError" class="picker-error">
      <span class="picker-error-text">{{ $t('qr.tables.loadError') }}</span>
      <PosUiButton
        color="neutral"
        variant="outline"
        size="xs"
        icon="refresh"
        @click="emit('retry')"
      >
        {{ $t('qr.tables.retry') }}
      </PosUiButton>
    </div>

    <div v-else-if="loading || !tables" class="picker-skeleton">
      <PosUiSkeleton v-for="n in 4" :key="n" class="skeleton-row" />
    </div>

    <PosUiEmptyState
      v-else-if="tables.length === 0"
      icon="table-furniture"
      :title="String($t('qr.tables.empty.title'))"
      :description="String($t('qr.tables.empty.description'))"
    >
      <template v-if="canCreateTables" #action>
        <PosUiButton color="primary" size="sm" icon="plus" @click="goToTables">
          {{ $t('qr.tables.empty.action') }}
        </PosUiButton>
      </template>
    </PosUiEmptyState>

    <template v-else>
      <div class="picker-toolbar">
        <PosUiInput
          class="picker-search"
          :value="query"
          icon="magnify"
          size="sm"
          clearable
          :placeholder="String($t('qr.tables.search'))"
          @input="query = $event"
        />
        <span class="picker-count">
          {{
            $t('qr.tables.selectedCount', {
              selected: selectedIds.length,
              total: tables.length,
            })
          }}
        </span>
        <div class="picker-actions">
          <PosUiButton
            color="neutral"
            variant="ghost"
            size="xs"
            @click="selectAll"
          >
            {{ $t('qr.tables.selectAll') }}
          </PosUiButton>
          <PosUiButton
            color="neutral"
            variant="ghost"
            size="xs"
            @click="clearAll"
          >
            {{ $t('qr.tables.clearAll') }}
          </PosUiButton>
        </div>
      </div>

      <div class="tables-list">
        <label
          v-for="table in filteredTables"
          :key="table.id"
          class="table-row"
        >
          <input
            type="checkbox"
            class="table-checkbox"
            :checked="isSelected(table.id)"
            @change="onToggle(table.id, $event)"
          />
          <span class="table-name">{{ table.name }}</span>
        </label>
        <p v-if="filteredTables.length === 0" class="no-results">
          {{ $t('qr.tables.noResults') }}
        </p>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .tables-picker {
    @apply tw-flex tw-flex-col tw-gap-3;

    .picker-error {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-bg-red-50 tw-px-3 tw-py-2;

      .picker-error-text {
        @apply tw-text-sm tw-text-red-700;
      }
    }

    .picker-skeleton {
      @apply tw-flex tw-flex-col tw-gap-2;

      .skeleton-row {
        @apply tw-h-11 tw-w-full;
      }
    }

    .picker-toolbar {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

      .picker-search {
        @apply tw-w-full sm:tw-w-56;
      }

      .picker-count {
        @apply tw-text-xs tw-text-neutral-500;
      }

      .picker-actions {
        @apply tw-ml-auto tw-flex tw-items-center tw-gap-1;
      }
    }

    .tables-list {
      @apply tw-max-h-72 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200;

      .table-row {
        @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-3 tw-border-b tw-border-solid tw-border-neutral-100 tw-px-3 tw-py-2;

        &:last-child {
          @apply tw-border-b-0;
        }

        &:hover {
          @apply tw-bg-neutral-50;
        }

        .table-checkbox {
          @apply tw-h-5 tw-w-5 tw-cursor-pointer tw-accent-orange-600;
        }

        .table-name {
          @apply tw-text-sm tw-text-neutral-900;
        }
      }

      .no-results {
        @apply tw-m-0 tw-px-3 tw-py-4 tw-text-sm tw-text-neutral-500;
      }
    }
  }
</style>
